import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import BlueDotsBackground from '../../BlueDotsBackground';
import images from '../../content/gpoContent';

const automation = [
  {
    iconClass: 'fas fa-link',
    title: 'Integrated with GPOs',
    description:
      'OpenPharma seamlessly ingests GPO rosters and links them directly to your existing customer records. No manual work required.',
    image: images[0],
  },
  {
    iconClass: 'fas fa-file-arrow-up',
    title: 'Update Eligibility',
    description:
      'GPO rosters are systematically matched against eligibility lists to identify necessary updates.',
    image: images[1],
  },
  {
    iconClass: 'fas fa-shield-check',
    title: 'Validations',
    description:
      'OpenPharma proactively detects discrepancies between roster data and your existing customer records, detecting updates necessary to key fields like identifiers, addresses, and names.',
    image: images[2],
  },
  {
    iconClass: 'fas fa-file-lines',
    title: 'Reporting',
    description:
      'OpenPharma provides fully customizable reporting that connects your key master data and transactional data to the GPO rosters.',
    image: images[3],
  },
];

const benefits = [
  {
    iconClass: 'fas fa-check-circle',
    title: 'No More Manual Errors',
    description:
      'Eliminate time-consuming, error-prone spreadsheets and reduce operational risk.',
  },
  {
    iconClass: 'fas fa-globe-stand',
    title: 'Universal View',
    description:
      'OpenPharma ingests data from key industry sources — including HIBCC, HRSA, and more — to give you a complete, unified view of your members.',
  },
];

const differentiators = [
  {
    iconClass: 'fas fa-eye',
    title: 'Historical Visibility',
    description:
      'As rosters evolve, OpenPharma preserves full historical visibility across all member records, ensuring nothing is lost over time..',
  },
  {
    iconClass: 'fas fa-lightbulb',
    title: 'Strategic Insights',
    description:
      'OpenPharma turns membership and transaction data into real-time insights. Improve team collaboration and accelerate decision-making with intuitive dashboards and reports.',
  },
];

export default function GpoRosterManagement() {
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setHighlightedIndex((prev) => (prev + 1) % automation.length);
    }, 8000); // changes every 8 seconds

    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <BlueDotsBackground />
      <section
        id="hero"
        className="relative text-center text-white overflow-hidden font-sans"
      >
        <div className="relative z-10 px-6 min-h-screen flex flex-col justify-center">
          <div className="max-w-5xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-semibold tracking-tight leading-tight mb-8">
              Struggling to manage GPO rosters and eligibility?
            </h1>
            <p className="text-lg md:text-2xl text-gray-300 font-light mb-12">
              Kernel helps you stay compliant with automated GPO roster updates,
              validations, and member tracking - no more outdated files or
              missed claims.
            </p>
            <div className="max-w-xl w-full mx-auto mb-20"></div>
          </div>
        </div>
      </section>

      <section id="automation" className="py-16 px-6 text-white text-center">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-8">
            Automated End-To-End Process
          </h2>
          <div className="h-[100px] flex items-center justify-center mb-10">
            <img
              src={automation[highlightedIndex].image}
              alt="Automation Step Visual"
              className="h-full object-contain mx-auto rounded-lg shadow-lg"
            />
          </div>

          {/* Dynamic description under image */}
          <div className="h-[60px] mb-10 flex items-center justify-center transition-all duration-300">
            <p className="text-gray-300 text-base md:text-lg max-w-3xl mx-auto">
              {automation[highlightedIndex].description}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left text-gray-800">
            {automation.map((step, idx) => (
              <div
                key={idx}
                className={`bg-gray-900 border rounded-xl p-6 shadow-md flex flex-col items-start gap-4 ${
                  highlightedIndex === idx ?
                    'border-blue-400 shadow-xl scale-105 transition-all'
                  : 'border-gray-700'
                }`}
              >
                <i className={`${step.iconClass} text-4xl text-blue-400`} />
                <h3 className="text-xl font-semibold text-white">
                  {step.title}
                </h3>
                {/* <p className="text-gray-300 text-sm">{automation.description}</p> */}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* <section id="automation" className="py-16 px-6 text-white text-center">
                <div className="max-w-5xl mx-auto">
                    <h2 className="text-3xl md:text-4xl font-bold mb-8">Automated End to End Process</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left text-gray-800">
                        {automation.map((lifecycle, idx) => (
                            <div
                                key={idx}
                                className="bg-gray-900 border border-gray-700 rounded-xl p-6 shadow-md flex flex-col items-start gap-4"
                            >
                                <i className={`${lifecycle.iconClass} text-4xl text-blue-400`} />
                                <h3 className="text-xl font-semibold text-white">{lifecycle.title}</h3>
                                <p className="text-gray-300 text-sm">{lifecycle.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section> */}

      <section id="benefits" className="py-16 px-6 text-white text-center">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-8">Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left text-gray-800">
            {benefits.map((benefit, idx) => (
              <div
                key={idx}
                className="bg-gray-900 border border-gray-700 rounded-xl p-6 shadow-md flex flex-col items-start gap-4"
              >
                <i className={`${benefit.iconClass} text-4xl text-blue-400`} />
                <h3 className="text-xl font-semibold text-white">
                  {benefit.title}
                </h3>
                <p className="text-gray-300 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        id="differentiators"
        className="py-16 px-6 text-white text-center"
      >
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-8">
            What Sets Us Apart
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left text-gray-800">
            {differentiators.map((differentiator, idx) => (
              <div
                key={idx}
                className="bg-gray-900 border border-gray-700 rounded-xl p-6 shadow-md flex flex-col items-start gap-4"
              >
                <i
                  className={`${differentiator.iconClass} text-4xl text-blue-400`}
                />
                <h3 className="text-xl font-semibold text-white">
                  {differentiator.title}
                </h3>
                <p className="text-gray-300 text-sm">
                  {differentiator.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="cta" className="py-16 px-6 text-white text-center">
        <div className="max-w-6xl mx-auto flex flex-col items-center justify-center gap-6">
          <Link to="/contact">
            <button className="w-60 border border-blue-500 text-blue-300 hover:bg-blue-800 font-medium py-3 px-6 rounded-xl transition duration-200">
              Contact Us
            </button>
          </Link>
          <Link to="/login">
            <button className="w-60 border border-blue-500 text-blue-300 hover:bg-blue-800 font-medium py-3 px-6 rounded-xl transition duration-200">
              Explore Our Platform
            </button>
          </Link>
        </div>
      </section>
    </>
  );
}
